import type { Client } from '@frontend/api-types';
import type { GrowthBook } from '@frontend/react/growthbook';
import type { FeatureMap } from '@frontend/react/hooks/growthbook/helpers';
import { getSegment, identifyClient } from '@frontend/web-utils';
import { useEffect, useRef, useState } from 'react';
import type { useLazyRetrieveClientQuery, useLazyRetrieveClientStatusQuery } from './api';

type RetrieveClient = ReturnType<typeof useLazyRetrieveClientQuery>[0];
type RetrieveClientStatus = ReturnType<typeof useLazyRetrieveClientStatusQuery>[0];

type UseAppIdentityProps = {
  env: Parameters<typeof identifyClient>[1];
  fbclid: MaybeUndefined<string>;
  gclid: MaybeUndefined<string>;
  growthbook: GrowthBook<FeatureMap>;
  isSsoLoading: boolean;
  reduxClient: Client | undefined;
  retrieveClient: RetrieveClient;
  retrieveClientStatus: RetrieveClientStatus;
  storeClient: (client: Client) => void;
  token: string | undefined;
};

export const useAppIdentity = ({
  env,
  fbclid,
  gclid,
  growthbook,
  isSsoLoading,
  reduxClient,
  retrieveClient,
  retrieveClientStatus,
  storeClient,
  token,
}: UseAppIdentityProps) => {
  const [isIdentifyDone, setIsIdentifyDone] = useState(false);
  const authenticatedIdentityDepsRef = useRef({
    env,
    fbclid,
    gclid,
    growthbook,
    reduxClient,
    retrieveClient,
    retrieveClientStatus,
    storeClient,
  });

  useEffect(() => {
    // eslint-disable-next-line fp/no-mutation
    authenticatedIdentityDepsRef.current = {
      env,
      fbclid,
      gclid,
      growthbook,
      reduxClient,
      retrieveClient,
      retrieveClientStatus,
      storeClient,
    };
  }, [env, fbclid, gclid, growthbook, reduxClient, retrieveClient, retrieveClientStatus, storeClient]);

  useEffect(() => {
    if (!token) {
      return;
    }

    (async () => {
      const { env, fbclid, gclid, reduxClient, retrieveClient, retrieveClientStatus, storeClient } =
        authenticatedIdentityDepsRef.current;

      await retrieveClient().then(
        async ({ data: client }) => {
          if (client) {
            storeClient(client);
          }

          const identifiedClient = client ?? reduxClient;
          if (identifiedClient) {
            const clientStatus = await retrieveClientStatus()
              .unwrap()
              .catch(() => undefined);
            identifyClient(identifiedClient, env, clientStatus, { fbclid, gclid });
            setIsIdentifyDone(true);
          } else {
            setIsIdentifyDone(false);
          }
        },
        () => setIsIdentifyDone(true),
      );
    })();

    authenticatedIdentityDepsRef.current.growthbook.refreshFeatures();
  }, [token]);

  useEffect(() => {
    if (token) {
      return;
    }

    setIsIdentifyDone(false);
    if (isSsoLoading) {
      return;
    }

    const segment = getSegment();
    segment.ready(async () => {
      const anonymousId = (await segment.user()).anonymousId();

      if (anonymousId) {
        growthbook.setAttributes({ anonymousId });
      }
      await growthbook.refreshFeatures();
    });
  }, [growthbook, isSsoLoading, token]);

  return { isIdentifyDone };
};
